import { useCallback, useEffect, useState, type FormEvent } from 'react'
import AppHeader from '../../components/shell/AppHeader'
import SupervisorNav from '../../components/supervisor/SupervisorNav'
import EmptyState from '../../components/shell/EmptyState'
import TeamSwitcher from '../../components/supervisor/TeamSwitcher'
import { useProfileIdentity } from '../../lib/useProfileIdentity'
import { supabase } from '../../lib/supabase'
import { fullDate, todayIso } from '../reports/reportDates'
import { formatTimesInText, useTimeFormat } from '../../lib/timeFormat'
import { useSupervisorTeams } from './useSupervisorTeams'
import {
  cancelOpenShift,
  getAssignmentOptions,
  getShiftOptions,
  getTeamOpenShifts,
  postOpenShift,
  type AssignmentOption,
  type OpenShiftWriteResult,
  type ShiftOption,
  type TeamOpenShift,
} from './openShiftStore'

const fieldClass = 'min-h-11 w-full rounded-control border border-line-strong bg-canvas px-2.5 py-2 text-small'
const labelClass = 'mb-1 block text-caption font-bold text-ink-muted'

const STATE_LABEL = { open: 'Open', filled: 'Filled', cancelled: 'Cancelled' } as const

/**
 * docs/product/scheduling-model.md's "Open shifts": a supervisor posts a slot
 * on a published pay period, and the agents of the team claim it. Claims are
 * decided in the approval inbox, and an approval amends the published
 * schedule.
 */
export default function SupervisorOpenShiftsPage() {
  const identity = useProfileIdentity()
  const { format } = useTimeFormat()
  const [userId, setUserId] = useState<string | null>(null)
  const [sessionReady, setSessionReady] = useState(false)
  const { teams, currentTeamId, teamsLoaded, switchTeam } = useSupervisorTeams(userId)
  const [shifts, setShifts] = useState<TeamOpenShift[]>([])
  // The shifts and assignments loaded for one team. They stand for the team on
  // screen only while their own team id matches it; after a team switch they are
  // set aside until the new team's arrive, so nothing chosen for the other team
  // can reach a posting for this one.
  const [loadedOptions, setLoadedOptions] = useState<{ teamId: string; shifts: ShiftOption[]; assignments: AssignmentOption[] } | null>(null)

  const [date, setDate] = useState('')
  const [shiftId, setShiftId] = useState('')
  const [assignmentId, setAssignmentId] = useState('')
  const [note, setNote] = useState('')
  const [result, setResult] = useState<OpenShiftWriteResult | null>(null)

  const reload = useCallback(async () => {
    setShifts(await getTeamOpenShifts())
  }, [])

  useEffect(() => {
    let cancelled = false
    supabase.auth
      .getSession()
      .then(async ({ data: { session } }) => {
        if (cancelled) return
        const id = session?.user.id
        if (id) {
          setUserId(id)
          await reload()
        }
      })
      .finally(() => {
        if (!cancelled) setSessionReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [reload])

  useEffect(() => {
    if (!currentTeamId) return
    let cancelled = false
    Promise.all([getShiftOptions(currentTeamId), getAssignmentOptions(currentTeamId)]).then(([shifts, assignments]) => {
      if (!cancelled) setLoadedOptions({ teamId: currentTeamId, shifts, assignments })
    })
    return () => {
      cancelled = true
    }
  }, [currentTeamId])

  const teamOptions = loadedOptions?.teamId === currentTeamId ? loadedOptions : null
  const shiftOptions = teamOptions?.shifts ?? []
  const assignmentOptions = teamOptions?.assignments ?? []
  // A choice that is not among this team's options is not a choice: the first
  // shift stands in, and no assignment is chosen.
  const chosenShiftId = shiftOptions.some((option) => option.id === shiftId) ? shiftId : (shiftOptions[0]?.id ?? '')
  const chosenAssignmentId = assignmentOptions.some((option) => option.id === assignmentId) ? assignmentId : ''

  async function post(event: FormEvent) {
    event.preventDefault()
    if (!teamOptions) {
      setResult({ ok: false, message: 'The shifts and assignments for this team are still loading.' })
      return
    }
    const shift = shiftOptions.find((option) => option.id === chosenShiftId)
    if (!userId || !shift) {
      setResult({ ok: false, message: 'Choose a shift.' })
      return
    }
    if (!date) {
      setResult({ ok: false, message: 'Choose a date.' })
      return
    }
    const written = await postOpenShift(userId, { teamId: currentTeamId, date, shift, assignmentId: chosenAssignmentId || undefined, note })
    setResult(written)
    if (written.ok) {
      setDate('')
      setAssignmentId('')
      setNote('')
      await reload()
    }
  }

  async function cancel(id: string) {
    if (!userId) return
    const cancelled = await cancelOpenShift(userId, id)
    setResult(cancelled)
    if (cancelled.ok) await reload()
  }

  const teamName = teams.find((team) => team.id === currentTeamId)?.name ?? ''
  const teamShifts = shifts.filter((shift) => shift.teamId === currentTeamId)

  if (!sessionReady || (userId && !teamsLoaded)) {
    return (
      <div className="flex h-dvh flex-col bg-surface text-ink">
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-4">
          <p role="status" className="text-small text-ink-muted">
            Loading&hellip;
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-dvh flex-col bg-surface text-ink">
      <a
        href="#open-shifts-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-canvas focus:px-4 focus:py-2 focus:font-semibold"
      >
        Skip to main content
      </a>

      <AppHeader initials={identity?.initials ?? ''} />

      <main id="open-shifts-main" tabIndex={0} className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <SupervisorNav />
        <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-5">
          <div>
            <h1 className="text-h2 font-extrabold">Open Shifts</h1>
            <p className="mt-1 text-small text-ink-muted">
              {teamName} &middot; Post a slot on a published pay period for the agents of the team to claim. Claims are decided in the Approval Inbox.
            </p>
          </div>

          <TeamSwitcher teams={teams} currentTeamId={currentTeamId} onChange={switchTeam} />

          <form onSubmit={post} aria-label="Post an open shift" className="flex flex-col gap-3 rounded-surface border border-line bg-canvas p-4">
            <h2 className="text-small font-extrabold">Post an open shift</h2>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label htmlFor="open-shift-date" className={labelClass}>
                  Date
                </label>
                <input id="open-shift-date" type="date" min={todayIso()} value={date} onChange={(event) => setDate(event.target.value)} className={fieldClass} />
              </div>
              <div>
                <label htmlFor="open-shift-shift" className={labelClass}>
                  Shift
                </label>
                <select id="open-shift-shift" value={chosenShiftId} onChange={(event) => setShiftId(event.target.value)} className={fieldClass}>
                  {shiftOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {formatTimesInText(`${option.name}, ${option.startTime} to ${option.endTime}`, format)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="open-shift-assignment" className={labelClass}>
                  Assignment (optional)
                </label>
                <select id="open-shift-assignment" value={chosenAssignmentId} onChange={(event) => setAssignmentId(event.target.value)} className={fieldClass}>
                  <option value="">No assignment</option>
                  {assignmentOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="open-shift-note" className={labelClass}>
                Note (optional)
              </label>
              <input id="open-shift-note" type="text" maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} className={fieldClass} />
            </div>
            {result && !result.ok && (
              <p role="alert" className="text-caption font-semibold text-error">
                {result.message}
              </p>
            )}
            <button
              type="submit"
              className="flex min-h-11 items-center justify-center self-start rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover"
            >
              Post open shift
            </button>
          </form>

          <section aria-labelledby="open-shifts-list">
            <h2 id="open-shifts-list" className="text-caption font-bold tracking-wide text-ink-subtle uppercase">
              Posted shifts
            </h2>
            {teamShifts.length === 0 ? (
              <EmptyState>No open shifts have been posted for this team.</EmptyState>
            ) : (
              <ul className="mt-2 flex flex-col gap-3">
                {teamShifts.map((shift) => (
                  <li key={shift.id} className="rounded-surface border border-line bg-canvas p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-small font-bold">{fullDate(shift.date)}</p>
                        <p className="mt-0.5 text-small">
                          {formatTimesInText([shift.shiftName, `${shift.startTime} to ${shift.endTime}`, shift.assignmentName].filter(Boolean).join(', '), format)}
                        </p>
                        {shift.releasedByName && <p className="mt-0.5 text-small text-ink-muted">Given away by {shift.releasedByName}</p>}
                        {shift.note && <p className="mt-0.5 text-small text-ink-muted">{shift.note}</p>}
                      </div>
                      <span className="rounded-full bg-surface-2 px-2.5 py-0.5 text-caption font-bold whitespace-nowrap">{STATE_LABEL[shift.state]}</span>
                    </div>
                    {shift.state === 'open' && (
                      <div className="mt-2 flex items-center justify-between gap-3">
                        <p className="text-small text-ink-muted">
                          {shift.pendingClaims === 0 ? 'No claims yet' : shift.pendingClaims === 1 ? '1 claim waiting on a decision' : `${shift.pendingClaims} claims waiting on a decision`}
                        </p>
                        <button
                          type="button"
                          onClick={() => cancel(shift.id)}
                          className="flex min-h-11 items-center px-2 text-small font-bold text-primary"
                        >
                          Cancel
                          <span className="sr-only"> the open shift on {fullDate(shift.date)}</span>
                        </button>
                      </div>
                    )}
                    {shift.state === 'filled' && shift.filledByName && <p className="mt-2 text-small text-ink-muted">Filled by {shift.filledByName}.</p>}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </main>
    </div>
  )
}
